import React, { Component } from 'react';
import { Redirect, Route, Router, Switch } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ApolloProvider } from 'react-apollo';
import { SnackbarProvider } from 'notistack';
import { MuiThemeProvider } from '@material-ui/core/styles';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';

import {
  ChatComponent,
  LoggerComponent,
  SnackbarManager
} from './components/common';

import {
  CalculatorAwait,
  CompanyInfo,
  ConfidentialityPage,
  CustomerDetail,
  Error as ErrorPage,
  Feedback,
  Home,
  Login,
  PaymentRedirect,
  Payments,
  PaymentSuccess,
  PostBox,
  ReturnGoods,
  SAPIframe,
  Thanks,
  Version
} from './pages/index.troy';
import MainLayout from './containers/MainLayout/MainLayout.troy';
import HomeContainer from './containers/HomeContainer/index.troy';

import client from './config/graphql';
import history from './config/history';
import store from './store';
import { troyTheme } from './theme';
import { sagaMiddleware } from './middleware';
import saga from './sagas';
import logger from './utils/logger';

import { connectTo } from './utils';
import {
  clearStore,
  clearURLHistory,
  getDecisions,
  getJWTOnOneTimeToken,
  handleError,
  logout,
  restoreSession,
  sapAuthentication,
  setBusinessPartnerID,
  setCustomHeaders,
  setErrorStatus,
  setToken
} from './actions';

import * as routes from './constants/routes';
import { NOT_FOUND } from './constants/errorStatuses';

import { getSearchUrl, getUrlParams } from './utils/httpUtil';
import { isTokenValid } from './utils/jwt';
import { languageFromFullCode } from './constants/browser';

const headerRoutes = [{ path: routes.POST_BOX, component: PostBox }];

const AVAILABLE_HOMECONTAINER_ROUTES_AUTHENTICATED = [
  routes.ERROR,
  routes.COMPANY_INFO,
  routes.CONFIDENTIALITY,
  routes.THANKS,
  routes.FEEDBACK,
  routes.POST_BOX,
  routes.HOME,
  routes.PAYMENTS,
  routes.PAYMENT,
  routes.PAYMENT_REDIRECT,
  routes.PAYMENT_SUCCESS,
  routes.CALCULATOR_AWAIT,
  routes.RETURN_GOODS
];

const AVAILABLE_HOMECONTAINER_ROUTES = [
  routes.ERROR,
  routes.COMPANY_INFO,
  routes.CONFIDENTIALITY,
  routes.FEEDBACK,
  routes.THANKS
];

const UnAuthenticatedRoutes = ({ setErrorStatus }) => {
  return (
    <MainLayout>
      <Switch>
        <Route exact path="/" component={Login} />
        <Route path={routes.LOGIN} component={Login} />
        <Route path={routes.VERSION} component={Version} />
        <Route
          render={props => {
            return AVAILABLE_HOMECONTAINER_ROUTES.indexOf(
              props.location.pathname
            ) !== -1 ? (
              <HomeContainer>
                <Route path={routes.FEEDBACK} component={Feedback} />
                <Route path={routes.THANKS} component={Thanks} />
                <Route path={routes.COMPANY_INFO} component={CompanyInfo} />
                <Route
                  path={routes.CONFIDENTIALITY}
                  component={ConfidentialityPage}
                />
                <Route path={routes.ERROR} component={ErrorPage} />
              </HomeContainer>
            ) : (
              (() => {
                setErrorStatus(NOT_FOUND);
                return <Redirect to={routes.ERROR} />;
              })()
            );
          }}
        />
      </Switch>
    </MainLayout>
  );
};

const AuthenticatedRoutes = ({ setErrorStatus }) => {
  return (
    <Switch>
      <Route
        path={routes.CUSTOMER_DETAIL}
        render={props => <CustomerDetail isAuthenticated {...props} />}
      />
      <Route
        path={routes.SAP_IFRAME}
        component={props => <SAPIframe isAuthenticated {...props} />}
      />
      <Route
        path={routes.LOGIN}
        component={props => (
          <MainLayout reLoginMode>
            <Login {...props} />
          </MainLayout>
        )}
      />
      <Route path={routes.VERSION} component={Version} />
      <MainLayout isAuthenticated>
        <Route
          render={props => {
            return AVAILABLE_HOMECONTAINER_ROUTES_AUTHENTICATED.indexOf(
              props.location.pathname
            ) !== -1 ? (
              <HomeContainer isAuthenticated>
                <Route exact path="/" component={Home} />

                {headerRoutes.map((route, key) => (
                  <Route {...route} key={key} />
                ))}
                <Route path={routes.THANKS} component={Thanks} />
                <Route path={routes.FEEDBACK} component={Feedback} />
                <Route path={routes.PAYMENTS} component={Payments} />
                <Route path={routes.PAYMENT} component={Payments} />
                <Route
                  path={routes.PAYMENT_SUCCESS}
                  component={PaymentSuccess}
                />
                <Route
                  path={routes.PAYMENT_REDIRECT}
                  component={PaymentRedirect}
                />
                <Route
                  path={routes.CALCULATOR_AWAIT}
                  component={CalculatorAwait}
                />
                <Route path={routes.COMPANY_INFO} component={CompanyInfo} />
                <Route
                  path={routes.CONFIDENTIALITY}
                  component={ConfidentialityPage}
                />
                <Route path={routes.RETURN_GOODS} component={ReturnGoods} />
                <Route
                  exact
                  path={routes.ERROR}
                  component={props => <ErrorPage isAuthenticated {...props} />}
                />
              </HomeContainer>
            ) : (
              (() => {
                setErrorStatus(NOT_FOUND);
                return <Redirect to={routes.ERROR} />;
              })()
            );
          }}
        />
      </MainLayout>
    </Switch>
  );
};

class AppRouter extends Component {
  constructor(props) {
    super(props);

    const pathname = window.location.pathname;
    const {
      oneTimeToken: oneTimeTokenFromUrl,
      jwt: tokenFromUrl,
      businessPartnerID,
      ...urlParams
    } = getUrlParams();

    this.state = {
      isSAPIframe: pathname && pathname === routes.SAP_IFRAME,
      isCustomerDetail: pathname && pathname === routes.CUSTOMER_DETAIL,

      oneTimeToken: oneTimeTokenFromUrl || null,
      jwtToken: tokenFromUrl || null,
      isTokenLoading: !!oneTimeTokenFromUrl,

      businessPartnerId: businessPartnerID || null,
      isBusinessPartnerIdLoading: !!businessPartnerID,

      urlParams
    };
  }

  handleCustomHeaders = () => {
    const { setCustomHeaders } = this.props;
    const { urlParams } = this.state;
    if (Object.keys(urlParams).length) {
      setCustomHeaders(urlParams);
    }
  };

  handleTokenGet = () => {
    const { clearStore, clearURLHistory, getJWTOnOneTimeToken } = this.props;
    const { urlParams, oneTimeToken, jwtToken } = this.state;

    if (oneTimeToken || jwtToken) {
      if (oneTimeToken) {
        clearStore();
        getJWTOnOneTimeToken(oneTimeToken);
      }

      clearURLHistory(
        getSearchUrl({
          url: window.location.pathname,
          query: urlParams
        })
      );
    }
  };

  handleSapAuthorize = () => {
    const { setBusinessPartnerID, sapAuthentication } = this.props;
    const { isSAPIframe, businessPartnerId } = this.state;

    if (isSAPIframe && businessPartnerId) {
      try {
        setBusinessPartnerID(businessPartnerId);
        sapAuthentication(businessPartnerId);
      } catch (err) {}
    }
  };

  handleRestoreSession = () => {
    const { token, restoreSession, getDecisions } = this.props;
    const { oneTimeToken, isSAPIframe, businessPartnerId } = this.state;

    if (!!token && !oneTimeToken && !(isSAPIframe && businessPartnerId)) {
      restoreSession();
    } else {
      if (!oneTimeToken) {
        // oneTimeToken will ask decisions after logging in from saga
        getDecisions();
      }
    }
  };

  componentDidMount() {
    this.handleCustomHeaders();
    this.handleTokenGet();
    this.handleSapAuthorize();
    this.handleRestoreSession();
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const newState = {};
    if (prevState.isBusinessPartnerIdLoading && nextProps.authToken) {
      newState.isBusinessPartnerIdLoading = false;
      newState.businessPartnerId = null;
    }

    return Object.keys(newState).length ? newState : null;
  }

  handleTokenUpdate = () => {
    const { token, decisions } = this.props;
    const { isTokenLoading } = this.state;

    if (isTokenLoading && !!token && Object.keys(decisions).length > 0) {
      this.setState({ isTokenLoading: false, oneTimeToken: null });
    }
  };

  componentDidUpdate(prevProps, prevState) {
    this.handleTokenUpdate(prevProps);
  }

  shouldComponentUpdate(nextProps, nextState) {
    if (this.props.error !== nextProps.error && !nextState.isTokenLoading) {
      return false;
    }
    return true;
  }

  render() {
    const {
      token,
      chatShow,
      chatBotShow,
      setErrorStatus,
      language
    } = this.props;

    const { isTokenLoading, isBusinessPartnerIdLoading } = this.state;
    const isAuthenticated = isTokenValid(token);

    return isBusinessPartnerIdLoading || isTokenLoading ? null : (
      <MuiPickersUtilsProvider
        utils={MomentUtils}
        locale={languageFromFullCode(language)}
      >
        <MuiThemeProvider theme={troyTheme}>
          <SnackbarProvider>
            <Router history={history}>
              {!isAuthenticated ? (
                <UnAuthenticatedRoutes setErrorStatus={setErrorStatus} />
              ) : (
                <AuthenticatedRoutes setErrorStatus={setErrorStatus} />
              )}
            </Router>
            <SnackbarManager />
            {!this.state.isSAPIframe &&
              !this.state.isCustomerDetail &&
              !window.Cypress && (
                <ChatComponent
                  isAuthenticated={isAuthenticated}
                  chatShow={chatShow}
                  chatBotShow={chatBotShow}
                />
              )}
          </SnackbarProvider>
        </MuiThemeProvider>
      </MuiPickersUtilsProvider>
    );
  }
}

const ConnectedAppRouter = connectTo(
  ({
    decisions,
    auth: { token },
    sap: { businessPartnerID, authToken },
    ui: {
      chat: { show: chatShow },
      chatBot: { show: chatBotShow }
    },
    error: { info: error },
    translations: { language }
  }) => ({
    token,
    businessPartnerID,
    authToken,
    chatShow,
    chatBotShow,
    decisions,
    error,
    language
  }),
  {
    logout,
    setToken,
    getDecisions,
    getJWTOnOneTimeToken,
    setBusinessPartnerID,
    clearStore,
    setErrorStatus,
    clearURLHistory,
    setCustomHeaders,
    sapAuthentication,
    handleError,
    restoreSession
  },
  AppRouter
);

const App = () => (
  <LoggerComponent logger={logger}>
    <Provider store={store}>
      <ApolloProvider client={client}>
        <ConnectedAppRouter />
      </ApolloProvider>
    </Provider>
  </LoggerComponent>
);

export default App;

sagaMiddleware.run(saga);
